import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Calendar, TrendingUp, TrendingDown, Minus, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { usePdfExport } from '@/hooks/usePdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScheduleVsDispatch() {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('all');
  const { exporting, handleExport: exportPdf } = usePdfExport('/export/schedule-vs-dispatch-pdf', 'Schedule_vs_Dispatch');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/masters/customers`, {});
      setCustomers(res.data);
    } catch (err) { console.error("Failed to load customers:", err); }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/reports/schedule-vs-dispatch?month=${filterMonth}`;
      if (filterCustomer && filterCustomer !== 'all') url += `&customer_id=${filterCustomer}`;
      const response = await axios.get(url, {
        
      });
      setReport(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterCustomer]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);


  const getStatusIcon = (balance) => {
    if (balance > 0) return <TrendingDown className="w-4 h-4 inline text-red-600" />;
    if (balance < 0) return <TrendingUp className="w-4 h-4 inline text-green-600" />;
    return <Minus className="w-4 h-4 inline text-gray-600" />;
  };

  const getStatusColor = (balance) => {
    if (balance > 0) return 'bg-red-50 text-red-700 border-red-200';
    if (balance < 0) return 'bg-green-50 text-green-700 border-green-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="schedule-vs-dispatch-title">SCHEDULE VS DISPATCH</h1>
          <p className="text-sm text-text-muted mt-2">Track scheduled vs actual dispatch with current part status</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="export-pdf-button"
            onClick={() => exportPdf({ month: filterMonth })}
            disabled={exporting || report.length === 0}
            className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-medium"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {exporting ? 'EXPORTING...' : 'EXPORT PDF'}
          </Button>
          <Button
            data-testid="refresh-report-button"
            onClick={fetchReport}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            REFRESH
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border shadow-sm p-4">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Calendar className="w-4 h-4 text-text-muted" />
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Month</Label>
            </div>
            <Input
              type="month"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="w-48 rounded-none"
              data-testid="filter-month-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5 block">Customer</Label>
            <Select value={filterCustomer} onValueChange={setFilterCustomer}>
              <SelectTrigger data-testid="filter-customer-select" className="w-56 rounded-none">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.customer_id} value={c.customer_id}>
                    {c.customer_name || c.customer_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-text-muted">Loading report...</div>
        ) : report.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            No schedules found for {filterMonth}. Create schedules first.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part Name</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Customer</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Type</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Scheduled</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Dispatched</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Balance</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Current Stage</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor WIP</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">In-Process</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Net Pending</th>
                </tr>
              </thead>
              <tbody>
                {report.map((item, idx) => (
                  <tr key={idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`report-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs">{item.part_id}</td>
                    <td className="p-4 align-middle">{item.part_name || '-'}</td>
                    <td className="p-4 align-middle">{item.customer_name || item.customer_id}</td>
                    <td className="p-4 align-middle">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent ${
                        item.schedule_type === 'enhanced' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {item.schedule_type === 'enhanced' ? 'Enhanced' : 'Original'}
                      </span>
                    </td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{item.scheduled_qty}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{item.dispatched_qty}</td>
                    <td className="p-4 align-middle text-right">
                      <div className="flex items-center justify-end gap-2">
                        {getStatusIcon(item.balance_qty)}
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold font-mono ${getStatusColor(item.balance_qty)}`}>
                          {item.balance_qty}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 align-middle">
                      {item.current_stages && item.current_stages.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {item.current_stages.map((stage, sidx) => (
                            <span key={sidx} className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold border-transparent bg-amber-50 text-amber-700">
                              {stage.stage}: {stage.qty}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-text-muted text-xs">No Stock</span>
                      )}
                    </td>
                    <td className="p-4 align-middle text-right">
                      {item.vendor_wip && item.vendor_wip.length > 0 ? (
                        <div className="flex flex-wrap gap-1 justify-end">
                          {item.vendor_wip.map((w, widx) => (
                            <span key={widx} className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold border-transparent bg-purple-50 text-purple-700" data-testid={`vendor-wip-badge-${idx}-${widx}`}>
                              {w.vendor_name}({w.stage}): {w.qty}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-text-muted text-xs font-mono">0</span>
                      )}
                    </td>
                    <td className="p-4 align-middle text-right font-mono text-xs font-semibold">
                      {(item.in_process_total || 0).toLocaleString()}
                    </td>
                    <td className="p-4 align-middle text-right font-mono text-xs font-semibold">
                      <span className={(item.net_pending || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}>
                        {(item.net_pending || 0).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-red-50 border border-red-200 p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-red-600" />
            <h3 className="text-sm font-semibold text-red-900 uppercase tracking-wide">Behind Schedule</h3>
          </div>
          <p className="text-xs text-red-800">Balance > 0 means dispatch is less than scheduled</p>
        </div>
        
        <div className="bg-green-50 border border-green-200 p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <h3 className="text-sm font-semibold text-green-900 uppercase tracking-wide">Ahead of Schedule</h3>
          </div>
          <p className="text-xs text-green-800">Balance &lt; 0 means dispatch is more than scheduled</p>
        </div>
        
        <div className="bg-gray-50 border border-gray-200 p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Minus className="w-4 h-4 text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">On Schedule</h3>
          </div>
          <p className="text-xs text-gray-800">Balance = 0 means dispatch matches scheduled exactly</p>
        </div>
      </div>
    </div>
  );
}
